import React, { FC, memo, useContext, useEffect, useRef, useState } from 'react'

import { useMutation } from '@apollo/client'
import { isMobile } from 'react-device-detect'
import { FormProvider, useForm } from 'react-hook-form'
import { useWizardStep } from 'react-wizard-primitive'

import { UPDATE_CART_FULFILLMENT, AppContext, COUNTRY_STATE_ISO_CODE_REGEXP, OTHER } from 'Const'
import { useFulfillmentStepTexts, useFulfillmentKindAddress } from 'Hooks/checkout'
import { FormAddress } from 'Pages/Checkout/AddressFields'
import { CartFulfillmentCourierAction, CartFulfillmentKinds, Customer, DeliveryTypes } from 'Types'
import { scrollToTopOfElement } from 'Utils'
import { isASAP, isDelivery, isDropOff, isPickup, isShipping, isTable, someMenuItems } from 'Utils/cartFulfillment'

import ErrorMessage from 'Components/ErrorMessage'
import FulfillmentIcon from 'Components/FulfillmentIcon'

import { StepContext } from '..'
import FormErrors from '../FormErrors'
import NextStepBtn from '../NextStepBtn'

import FulfillmentAddress from './FulfillmentAddress'
import FulfillmentDateTime from './FulfillmentDateTime'

const FulfillmentKindTitles = {
  delivery: 'Delivery',
  tableside: 'Tableside',
  pickup: 'Pickup',
  shipping: 'Shipping',
  auto: 'Auto',
  drop_off: 'Drop-off',
}
const courierActionLabels = {
  leave_at_door: 'Leave at the door',
  meet_at_door: 'Meet at the door',
}
export interface FormState {
  fulfillment: {
    courierAction: CartFulfillmentCourierAction
    customerAddressId?: string
    kind: CartFulfillmentKinds
  }
  locationId?: string
  shippingAddress?: FormAddress
}

// Checkout Step #2
const FultillmentFieldsGroup: FC = () => {
  const { cart, currentLocation, account } = useContext(AppContext)
  const { checkouting, isLastStep, nextStep } = useContext(StepContext)
  const form = useForm<FormState>()
  const stepContainerRef = useRef<HTMLFormElement>(null)
  const { register, setValue, watch, handleSubmit, getValues } = form
  const { isActive, hasBeenActive, resetToStep } = useWizardStep()

  useEffect(() => {
    if (isMobile && isActive) {
      scrollToTopOfElement(stepContainerRef)
    }
  }, [isActive])

  const [updateCartFulfillment, { loading }] = useMutation(UPDATE_CART_FULFILLMENT, {
    update(_, { data }) {
      const customer = data.updateCartFulfillment.customer as Customer | null
      if (customer) {
        setValue('fulfillment.customerAddressId', customer.primaryAddress?.id)
      }
      nextStep(getValues())
    },
  })

  const { hasDeliverableItems, hasPickupableItems, hasShippingableItems, fulfillment } = cart
  const firstAvailablePickupLocation = cart.availablePickupLocations[0]
  const [locationId, setLocationId] = useState(
    fulfillment
      ? fulfillment.location.id
      : firstAvailablePickupLocation && isPickup(fulfillment)
      ? firstAvailablePickupLocation.id
      : currentLocation.id,
  )

  const [resetButtonText, stepTitle] = useFulfillmentStepTexts()
  const kindAddress = useFulfillmentKindAddress()

  useEffect(() => {
    if (fulfillment?.courierAction) {
      setValue('fulfillment.courierAction', fulfillment.courierAction)
    }
  }, [fulfillment, setValue])

  const onSubmit = handleSubmit(({ fulfillment, locationId, shippingAddress }) => {
    let address
    if (shippingAddress) {
      const { stateIsoCode, ...rest } = shippingAddress
      const key = COUNTRY_STATE_ISO_CODE_REGEXP.test(stateIsoCode) ? 'stateIsoCode' : 'state'
      address = { ...rest, [key]: stateIsoCode }
    }
    if (fulfillment.customerAddressId == OTHER) {
      fulfillment.customerAddressId = undefined
    }
    void updateCartFulfillment({ variables: { ...fulfillment, locationId, address } })
  })

  const kind = watch('fulfillment.kind')
  const isCurrentNonPickup = kind !== CartFulfillmentKinds.PICKUP
  const isCurrentShipping = kind == CartFulfillmentKinds.SHIPPING

  const fulfillmentAddress = <FulfillmentAddress cssPrefix="shipping" nameGroupPrefix="shippingAddress" />
  let fulfillmentDateTime = null
  if (fulfillment) {
    fulfillmentDateTime = <FulfillmentDateTime fulfillment={fulfillment} isASAP={isASAP(fulfillment)} />
  }

  return (
    <FormProvider {...form}>
      <div className="checkout-step / mb-5" hidden={isActive || !hasBeenActive}>
        <h2 className="checkout-step__header / h4">
          {fulfillment ? (
            <>
              <FulfillmentIcon kind={fulfillment.kind} />
              <span>{FulfillmentKindTitles[fulfillment.kind]}</span>
            </>
          ) : null}
          <button className="checkout-button / ml-auto" type="button" onClick={resetToStep}>
            {resetButtonText}
          </button>
        </h2>

        {/* Fulfillment type and date */}
        {fulfillment ? (
          <p className="my-1" data-table-code={fulfillment?.tableCode}>
            {/* Hardcode for table side ordering for account with id 2114 */}
            {isTable(fulfillment) ? (
              +account?.id === 2114 ? (
                <>
                  You will be notified by text message when your order is ready for pick up. <br />
                  All orders from the Waterworks Bar will be delivered to{' '}
                  <strong className="text-uppercase">{fulfillment.tableCode}</strong>
                </>
              ) : (
                <>
                  Your order will be brought to <strong className="text-uppercase">{fulfillment.tableCode}</strong>
                </>
              )
            ) : isPickup(fulfillment) ? (
              <>
                Pickup from <strong>{fulfillment.location.name}</strong>
              </>
            ) : isDropOff(fulfillment) ? (
              `Drop-off at ${kindAddress}`
            ) : isShipping(fulfillment) ? (
              `Shipping to ${kindAddress}`
            ) : (
              `Delivery to ${kindAddress}`
            )}

            {!isTable(fulfillment) && fulfillment.currentFulfillmentAt ? (
              <span className="d-block / mt-1">{fulfillmentDateTime}</span>
            ) : null}
          </p>
        ) : null}
      </div>

      <form ref={stepContainerRef} hidden={!isActive}>
        <div className="checkout-step">
          <h2 className="checkout-step__header / h4">
            <FulfillmentIcon kind={fulfillment?.kind || CartFulfillmentKinds.PICKUP} />
            <span>{stepTitle}</span>
          </h2>

          <div className="checkout-form__errors" data-form="fulfillment_form"></div>

          <input ref={register} hidden disabled={isCurrentNonPickup} name="locationId" value={locationId} />

          {/* Table side ordering */}
          {isTable(fulfillment) ? (
            <>
              {+account?.id === 2114 ? (
                <p>
                  You will be notified by text message when your order is ready for pick up. <br />
                  All orders from the Waterworks Bar will be delivered to{' '}
                  <strong className="text-uppercase">{fulfillment.tableCode}</strong>
                </p>
              ) : (
                <p data-table-code={fulfillment.tableCode}>
                  Your order will be brought to <strong className="text-uppercase">{fulfillment.tableCode}</strong>
                </p>
              )}
              <input ref={register} defaultValue="tableside" name="fulfillment.kind" type="hidden" />
            </>
          ) : isPickup(fulfillment) && someMenuItems(cart) ? (
            fulfillment.currentFulfillmentAt ? (
              <>
                <input ref={register} defaultValue="pickup" name="fulfillment.kind" type="hidden" />
                Pickup from <strong>{fulfillment.location.name}</strong>
                <p className="mt-1">{fulfillmentDateTime}</p>
              </>
            ) : (
              'Sorry ASAP is not available for now'
            )
          ) : isDelivery(fulfillment) && hasDeliverableItems ? (
            <>
              <p className="mt-1 / mb-1">{fulfillmentDateTime}</p>
              {fulfillment.scheduleSetting?.deliveryType?.includes(DeliveryTypes.UBER) ? (
                <>
                  <label className="mt-4 / mb-2">Specify the action for the courier to take on a delivery</label>
                  <select
                    ref={register}
                    className="custom-select form-control"
                    defaultValue={CartFulfillmentCourierAction.LEAVE_AT_DOOR}
                    name="fulfillment.courierAction"
                  >
                    {Object.values(CartFulfillmentCourierAction).map((action) => (
                      <option key={action} value={action}>
                        {courierActionLabels[action]}
                      </option>
                    ))}
                  </select>
                </>
              ) : null}
              <input ref={register} defaultValue="delivery" name="fulfillment.kind" type="hidden" />
              <div className="mt-4">
                {fulfillmentAddress}
                <div className="checkout-form__errors" data-form="shipping_form"></div>
              </div>
            </>
          ) : isDropOff(fulfillment) ? (
            <>
              <input ref={register} defaultValue="drop_off" name="fulfillment.kind" type="hidden" />
              <p className="mt-1 / mb-1">{fulfillmentDateTime}</p>
              Drop-off at {kindAddress}
            </>
          ) : (
            <>
              <div>
                {/* Pickup options */}
                {hasPickupableItems ? (
                  <>
                    {cart.availablePickupLocations.map((location) => (
                      <div key={location.id} className="checkout-option">
                        <input
                          ref={register({ required: true })}
                          required
                          defaultValue="pickup"
                          id={`pick-up-${location.id}`}
                          name="fulfillment.kind"
                          type="radio"
                          onChange={() => setLocationId(location.id)}
                        />
                        <label className="checkout-option__control" htmlFor={`pick-up-${location.id}`}>
                          <div className="checkout-option__inner">
                            <p className="m-0 / pr-1">
                              Pickup from <strong>{location.name}</strong>
                              <br />
                              {location.address.address}, {location.address.city}
                            </p>
                          </div>
                        </label>
                      </div>
                    ))}
                  </>
                ) : null}

                {/* Shipping options */}
                {hasShippingableItems ? (
                  <div className="checkout-option">
                    <input
                      ref={register({ required: true })}
                      required
                      defaultValue="shipping"
                      id="shipping"
                      name="fulfillment.kind"
                      type="radio"
                    />
                    <label className="checkout-option__control" htmlFor="shipping">
                      <span className="checkout-option__inner">Shipping</span>
                    </label>
                  </div>
                ) : null}
              </div>

              {isCurrentShipping ? (
                <div className="mt-4">
                  {fulfillmentAddress}
                  <div className="checkout-form__errors" data-form="shipping_form" />
                </div>
              ) : null}
            </>
          )}
          <ErrorMessage as={FormErrors} name="fulfillment.kind" />
        </div>
        <NextStepBtn loading={loading || checkouting} {...{ isLastStep }} onSubmit={onSubmit} />
      </form>
    </FormProvider>
  )
}

export default memo(FultillmentFieldsGroup)
